/**
 * Whether an OCR result is text, or Tesseract's guess at texture.
 *
 * A photo with no real text (a wall, a fabric, a blurred shot) still comes back
 * from Tesseract as a string: «4 AGT», «p 9 | I)», «\ e~ J che». Nothing marked it
 * as unreadable, so every entry point (text import, the text creation flow, the
 * lookup palette) filled its text area with it. `recognize()` now applies this
 * and returns an empty text for an unreadable result, which every caller already
 * answers with its existing "no text found" state.
 *
 * The criterion, with the numbers it was chosen from (Tesseract.js v7 through the
 * app's own `recognize`, on synthetic photos, 2026-10-26):
 *  - real text, from clean to blurred 1 px with noise, rotated, low contrast, white
 *    on black, one word or one line: page confidence 90–96;
 *  - real text blurred 2 px with heavy noise (partly misread): 60;
 *  - non-text (noise, gradients, blobs, stripes, scattered marks, lines): 0–40.
 * 50 sits in the empty gap between 40 and 60.
 *
 * A second test catches a result Tesseract is confident about that is still not
 * words: fewer than 75 % of its non-space characters are letters or digits
 * («p 9 | I)» is 60 %, «\ e~ J che» is 71 %; the real text above is 90 % or more,
 * prose punctuation being the rest). It cannot catch a short, confident,
 * alphanumeric guess such as «4 AGT»: that is indistinguishable from a real short
 * label, and is left to the confidence test.
 */
export const OCR_MIN_CONFIDENCE = 50
export const OCR_MIN_LETTER_SHARE = 0.75

export function isReadableOcr(text: string, confidence: number): boolean {
  const compact = text.replace(/\s+/g, '')
  if (compact.length === 0) return false
  if (confidence < OCR_MIN_CONFIDENCE) return false
  const wordChars = compact.match(/[\p{L}\p{N}]/gu)?.length ?? 0
  return wordChars / compact.length >= OCR_MIN_LETTER_SHARE
}
